import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import './LoginModal.css';

function LoginModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('login');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerFirstName, setRegisterFirstName] = useState('');
  const [registerLastName, setRegisterLastName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState('');
  const [registerGuard, setRegisterGuard] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState([]);
  const hasFetchedPasswordRequirements = useRef(false);
  const navigate = useNavigate();

  // Fetch password requirements the first time the modal opens to avoid test act warnings
  useEffect(() => {
    if (!isOpen || hasFetchedPasswordRequirements.current) {
      return undefined;
    }

    let isActive = true;

    const fetchPasswordRequirements = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/auth/password-requirements`,
        );
        const requirements = Array.isArray(response?.data?.requirements)
          ? response.data.requirements
          : [];
        if (isActive) {
          setPasswordRequirements(requirements);
          hasFetchedPasswordRequirements.current = true;
        }
      } catch (err) {
        console.error('Failed to fetch password requirements:', err);
        if (isActive) {
          setPasswordRequirements([]);
        }
      }
    };

    fetchPasswordRequirements();

    return () => {
      isActive = false;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const loginUrl = `${API_BASE_URL}/auth/login`;
    console.log('[LoginModal] Attempting login to:', loginUrl);
    console.log('[LoginModal] Email:', loginEmail);

    try {
      const response = await axios.post(
        loginUrl,
        {
          email: loginEmail,
          password: loginPassword,
        },
        {
          withCredentials: true,
        },
      );

      console.log('[LoginModal] Login successful:', response.status);

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Dispatch custom event to update navigation
      window.dispatchEvent(new Event('login'));

      // Trigger light sync in the background
      axios
        .post(
          `${API_BASE_URL}/strava/light-sync`,
          {
            email: response.data.user.email,
          },
          {
            withCredentials: true,
          },
        )
        .catch((err) => {
          console.log(
            'Background sync skipped:',
            err.response?.data?.error || err.message,
          );
        });

      onClose();
      navigate('/dashboard');
    } catch (err) {
      console.error('[LoginModal] Login failed:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        url: loginUrl,
        email: loginEmail,
      });
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Login failed. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!registerFirstName.trim() || !registerLastName.trim()) {
        throw new Error('Please provide both your first and last name.');
      }

      if (registerGuard) {
        throw new Error(
          'Registration blocked. Please contact support if this is a mistake.',
        );
      }

      if (registerPassword !== registerPasswordConfirm) {
        throw new Error('Passwords do not match.');
      }

      const response = await axios.post(
        `${API_BASE_URL}/auth/register`,
        {
          first_name: registerFirstName.trim(),
          last_name: registerLastName.trim(),
          email: registerEmail,
          password: registerPassword,
          signup_guard: registerGuard,
        },
        {
          withCredentials: true,
        },
      );

      // User is now logged in immediately after registration
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Dispatch custom event to update navigation
      window.dispatchEvent(new Event('login'));

      // Trigger light sync in the background
      axios
        .post(
          `${API_BASE_URL}/strava/light-sync`,
          {
            email: response.data.user.email,
          },
          {
            withCredentials: true,
          },
        )
        .catch((err) => {
          console.log(
            'Background sync skipped:',
            err.response?.data?.error || err.message,
          );
        });

      onClose();
      navigate('/dashboard');

      // Show verification reminder if email not verified
      if (!response.data.user.email_verified) {
        // Set a small timeout to let the page load first
        setTimeout(() => {
          alert(
            response.data.message ||
              'Registration successful! Please check your email to verify your account. Your account will be deleted if not verified within 7 days.',
          );
        }, 500);
      }
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Registration failed. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-card">
        <div className="modal-header">
          <button className="modal-close" onClick={onClose}>
            X
          </button>
          <h2>Welcome to Runstreak Dashboard</h2>
          <p>Login or create an account to start tracking your streak</p>
        </div>

        <div className="modal-content">
          {error && <div className="error-message">{error}</div>}

          <div className="tabs">
            <div className="tabs-list">
              <button
                className={`tab-trigger ${activeTab === 'login' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('login');
                  setError('');
                }}
              >
                Login
              </button>
              <button
                className={`tab-trigger ${activeTab === 'register' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('register');
                  setError('');
                }}
              >
                Register
              </button>
            </div>

            {activeTab === 'login' && (
              <div className="tab-content">
                <form onSubmit={handleLoginSubmit}>
                  <div className="form-group">
                    <label htmlFor="login-email">Email</label>
                    <input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="login-password">Password</label>
                    <input
                      id="login-password"
                      type="password"
                      placeholder="********"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Logging in...' : 'Login'}
                  </button>
                  <div className="forgot-password">
                    <a href="#forgot">Forgot password?</a>
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'register' && (
              <div className="tab-content">
                <form onSubmit={handleRegisterSubmit}>
                  <div className="form-group">
                    <label htmlFor="register-first-name">First name</label>
                    <input
                      id="register-first-name"
                      type="text"
                      placeholder="John"
                      value={registerFirstName}
                      onChange={(e) => setRegisterFirstName(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="register-last-name">Last name</label>
                    <input
                      id="register-last-name"
                      type="text"
                      placeholder="Doe"
                      value={registerLastName}
                      onChange={(e) => setRegisterLastName(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="register-email">Email</label>
                    <input
                      id="register-email"
                      type="email"
                      placeholder="you@example.com"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="register-password">Password</label>
                    <input
                      id="register-password"
                      type="password"
                      placeholder="********"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                    {passwordRequirements.length > 0 && (
                      <div
                        style={{
                          fontSize: '0.85rem',
                          marginTop: '0.5rem',
                          color: '#666',
                        }}
                      >
                        <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                          Password must meet the following requirements:
                        </div>
                        <ul
                          style={{
                            margin: '0',
                            paddingLeft: '1.5rem',
                            listStyle: 'disc',
                          }}
                        >
                          {passwordRequirements.map((req, index) => (
                            <li key={index}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="register-password-confirm">
                      Confirm password
                    </label>
                    <input
                      id="register-password-confirm"
                      type="password"
                      placeholder="********"
                      value={registerPasswordConfirm}
                      onChange={(e) =>
                        setRegisterPasswordConfirm(e.target.value)
                      }
                      required
                      disabled={loading}
                    />
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      left: '-10000px',
                      top: 'auto',
                      width: '1px',
                      height: '1px',
                      overflow: 'hidden',
                    }}
                    aria-hidden="true"
                  >
                    <label htmlFor="signup-guard">Leave this field blank</label>
                    <input
                      id="signup-guard"
                      type="text"
                      value={registerGuard}
                      onChange={(e) => setRegisterGuard(e.target.value)}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginModal;
